import { useEffect, useState, useMemo, useRef } from "react";
import CustomerCustomTab from "./components/CustomerCustomTab/CustomerCustomTab";
import CustomerDataTable from "./CustomerDataTable.js";
import "./CustomerTableV2.css";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import {
  // customerSearch,
  getAllUserDetails,
  getAppCustomers,
  getB2BCustomerEndpoint,
  getPosCustomersByLocation,
  getCreditOrders,
  customerSearchV2Api,
} from "../../../../config/Services";
import { useSelector } from "react-redux";
import { getCustomerText } from "../all-products/redux/Reducers/paginationReducer";
import { useSnackbar } from "../hooks/SnackbarProvider";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";

// ── Search icon SVG ──
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m21 21-4.34-4.34" />
    <circle cx="11" cy="11" r="8" />
  </svg>
);

const CustomerTableV2 = () => {
  const showSnackbar = useSnackbar();
  const [activeTab, setActiveTab] = useState("b2b");

  const [b2bData, setB2bData] = useState([]);
  const [b2bTotal, setB2bTotal] = useState(0);
  const [b2bPage, setB2bPage] = useState(0);
  const [b2bLoading, setB2bLoading] = useState(false);

  const [posData, setPosData] = useState([]);
  const [posTotal, setPosTotal] = useState(0);
  const [posPage, setPosPage] = useState(0);
  const [posLoading, setPosLoading] = useState(false);

  const [appData, setAppData] = useState([]);
  const [appTotal, setAppTotal] = useState(0);
  const [appPage, setAppPage] = useState(0);
  const [appLoading, setAppLoading] = useState(false);

  const [creditData, setCreditData] = useState([]);
  const [creditTotal, setCreditTotal] = useState(0);
  const [creditPage, setCreditPage] = useState(0);
  const [creditLoading, setCreditLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  const navigate = useNavigate();

  // ── Read search result from Redux ──
  const customer_text = useSelector(getCustomerText);

  const customerTabs = [
    { id: "b2b", label: "B2B Customers" },
    { id: "pos", label: "POS Customers" },
    { id: "app", label: "App Customers" },
    { id: "credit", label: "Credit Customers" },
  ];

  // B2B columns
  const b2bColumns = [
    { key: "name", label: "CUSTOMER NAME", width: "25%" },
    { key: "companyName", label: "COMPANY NAME", width: "25%" },
    { key: "phoneNumber", label: "MOBILE NUMBER", width: "20%" },
    { key: "gstNumber", label: "GSTIN", width: "15%" },
    { key: "updated", label: "LAST MODIFIED", width: "15%" },
  ];

  // POS columns
  const posColumns = [
    { key: "name", label: "Customer Name", width: "25%" },
    { key: "mobileNumber", label: "Contact Number", width: "25%" },
    { key: "email", label: "Email", width: "25%" },
    { key: "createdAt", label: "Created At", width: "25%" },
  ];

  // App columns
  const appColumns = [
    { key: "name", label: "Customer Name", width: "25%" },
    { key: "phoneNumber", label: "Contact Number", width: "25%" },
    { key: "emailId", label: "Email", width: "25%" },
    { key: "created", label: "Created At", width: "25%" },
  ];

  // Credit columns
  const creditColumns = [
    { key: "name", label: "CUSTOMER NAME", width: "25%" },
    { key: "companyName", label: "COMPANY NAME", width: "25%" },
    { key: "phoneNumber", label: "MOBILE NUMBER", width: "20%" },
    { key: "gstNumber", label: "GSTIN", width: "15%" },
    { key: "updated", label: "LAST MODIFIED", width: "15%" },
  ];

  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date)) return "N/A";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  // ── Map search results based on active tab ──
  const searchData = useMemo(() => {
    if (!customer_text) return null;

    const customers = customer_text.r?.data?.customers || [];

    if (customers.length === 0) return [];

    switch (activeTab) {
      case "b2b":
        return customers?.map((c) => ({
          id: c?.retailId || c?.id,
          name: c?.displayName || c?.name || "N/A",
          subLabel: c?.name || "N/A",
          companyName: c?.displayName || "N/A",
          phoneNumber: c?.phoneNumber || c?.mobileNumber || "N/A",
          gstNumber: c?.gstNumber || "N/A",
          updated: formatDate(c?.updated),
        }));

      case "pos":
        return customers?.map((c) => ({
          id: c?.id,
          uidx: c?.uidx || c?.id,
          name: c?.name || c?.firstName || "N/A",
          mobileNumber: c?.mobileNumber || "N/A",
          email: c?.email || "N/A",
          createdAt: formatDate(c?.created || c?.createdAt),
        }));

      case "app":
        return customers?.map((c) => ({
          id: c?.id,
          name: c?.name || "N/A",
          phoneNumber: c?.phoneNumber || "N/A",
          emailId: c?.emailId || c?.email || "N/A",
          created: formatDate(c?.created),
        }));

      default:
        return null;
    }
  }, [customer_text, activeTab]);

  async function getB2BCustomerEndpointFunction(page = 0, searchParameter = "") {
    setB2bLoading(true);
    const payload = {
      endDate: "",
      pageNumber: page,
      pageSize: 10,
      partnerId: orgId,
      partnerType: "RETAIL",
      searchParameter: searchParameter,
      startDate: "2023-01-01",
    };
    try {
      const response = await getB2BCustomerEndpoint(payload);
      const retails = response?.data?.data?.retails;
      const content = retails?.content || [];

      const mapped = content?.map((item) => ({
        id: item?.retailId,
        name: item?.displayName || item?.name || "N/A",
        subLabel: item?.name || "N/A",
        companyName: item?.displayName || "N/A",
        phoneNumber: item?.phoneNumber || "N/A",
        gstNumber: item?.gstNumber || "N/A",
        updated: formatDate(item?.updated),
      }));

      setB2bData(mapped);
      setB2bTotal(retails?.totalElements || 0);
    } catch (err) {
      showSnackbar("Failed to fetch customers", "error");
    } finally {
      setB2bLoading(false);
    }
  }

  async function getPosCustomerEndpointFunction(page = 0, searchParameter = "") {
    setPosLoading(true);
    // const payload = {
    //   endDate: "",
    //   locationId: locId,
    //   pageNumber: page,
    //   pageSize: 10,
    //   retailId: orgId,
    //   search: searchParameter,
    //   startDate: "2023-01-01",
    // };
    try {
      // const posLocationResponse = await getPosCustomersByLocation(payload);
      // const customers = posLocationResponse?.data?.data?.customers || [];
      // const totalCount = posLocationResponse?.data?.data?.count || 0;

      // const uidxList = customers?.map((el) => el?.uidx || "");
      // const detailsResponse = await getAllUserDetails({ uidx: uidxList });

      const payload = {
        organizationId: orgId,
        search: searchParameter,
        platform: ["POS"],
        pageNumber: page,
        pageSize: 10,
        organizationType: "RETAIL",
      };

      const getListOfCustomer = await customerSearchV2Api(payload);
      const customers = getListOfCustomer?.data?.customers || [];
      const totalCount = getListOfCustomer?.data?.totalRows || 0;

      // const details = detailsResponse?.data?.data || [];

      // const detailMap = {};
      // details?.forEach((d) => {
      //   detailMap[d.uidx] = d;
      // });

      const mapped = customers?.map((customer) => {
        // const detail = detailMap[customer?.uidx] || {};
        return {
          id: customer?.id,
          uidx: customer?.uidx || customer?.id,
          name: customer?.name || customer?.firstName || "N/A",
          mobileNumber: customer?.phoneNumber || customer?.mobileNumber || "N/A",
          email: customer?.emailId || customer?.email || "N/A",
          // createdAt: formatDate(customer?.created || detail?.createdAt),
          createdAt: formatDate(customer?.created),
        };
      });

      setPosData(mapped);
      setPosTotal(totalCount);
    } catch (err) {
      showSnackbar("Failed to fetch the result", "error");
    } finally {
      setPosLoading(false);
    }
  }

  async function getAppCustomersEndpointFunction(page = 0, searchParameter = "") {
    setAppLoading(true);
    // const payload = {
    //   organizationId: orgId,
    //   pageNumber: page,
    //   pageSize: 10,
    //   search: searchParameter || "",
    //   startDate: "",
    //   endDate: "",
    // };
    try {
      // const response = await getAppCustomers(payload);
      // const data = response?.data;
      // const customers = data?.customers || [];

      const payload = {
        organizationId: orgId,
        search: searchParameter,
        platform: ["APP"],
        pageNumber: page,
        pageSize: 10,
        organizationType: "RETAIL",
      };

      const getListOfCustomer = await customerSearchV2Api(payload);
      const customers = getListOfCustomer?.data?.customers || [];
      const totalCount = getListOfCustomer?.data?.totalRows || 0;

      const mapped = customers?.map((c) => ({
        id: c?.id,
        name: c?.name || c?.firstName || "N/A",
        phoneNumber: c?.phoneNumber || c?.mobileNumber || "N/A",
        emailId: c?.emailId || c?.email || "N/A",
        created: formatDate(c?.created),
      }));

      setAppData(mapped);
      setAppTotal(totalCount);
    } catch (err) {
      showSnackbar("Failed to fetch Customers", "error");
    } finally {
      setAppLoading(false);
    }
  }

  async function getCreditCustomersEndpointFunction(page = 0, searchParameter = "") {
    setCreditLoading(true);
    const payload = {
      searchBox: searchParameter || "",
      page: page,
      pageSize: 10,
      orderTypes: ["POS_ORDER", "WHATSAPP_ORDER"],
    };
    try {
      const response = await getCreditOrders(payload);
      const dataArr = response?.data?.data?.accounts || [];
      const mapped = dataArr?.map((row) => ({
        id: row?.accountNoteId,
        name: row?.customerName || "N/A",
        companyName: "-",
        phoneNumber: row?.mobileNumber || "N/A",
        gstNumber: "-",
        updated: formatDate(row?.updatedDate || row?.createdDate),
      }));
      setCreditData(mapped);
      setCreditTotal(response?.data?.data?.totalResults || 0);
    } catch (err) {
      showSnackbar("Failed to fetch Credit Customers", "error");
    } finally {
      setCreditLoading(false);
    }
  }

  // ── Unified fetch for active tab (handles both pagination and debouncedSearch) ──
  useEffect(() => {
    if (customer_text) return;
    if (activeTab === "b2b") getB2BCustomerEndpointFunction(b2bPage, debouncedSearch);
  }, [activeTab, b2bPage, customer_text, debouncedSearch]);

  useEffect(() => {
    if (customer_text) return;
    if (activeTab === "pos") getPosCustomerEndpointFunction(posPage, debouncedSearch);
  }, [activeTab, posPage, customer_text, debouncedSearch]);

  useEffect(() => {
    if (customer_text) return;
    if (activeTab === "app") getAppCustomersEndpointFunction(appPage, debouncedSearch);
  }, [activeTab, appPage, customer_text, debouncedSearch]);

  useEffect(() => {
    if (customer_text) return;
    if (activeTab === "credit") getCreditCustomersEndpointFunction(creditPage, debouncedSearch);
  }, [activeTab, creditPage, customer_text, debouncedSearch]);

  // ── Use searchData when available, fallback to fetched data ──
  const getTableProps = () => {
    switch (activeTab) {
      case "b2b":
        return {
          data: searchData ?? b2bData,
          total: searchData ? searchData?.length : b2bTotal,
          page: b2bPage,
          setPage: setB2bPage,
          columns: b2bColumns,
          loading: b2bLoading,
        };
      case "pos":
        return {
          data: searchData ?? posData,
          total: searchData ? searchData?.length : posTotal,
          page: posPage,
          setPage: setPosPage,
          columns: posColumns,
          loading: posLoading,
        };
      case "app":
        return {
          data: searchData ?? appData,
          total: searchData ? searchData?.length : appTotal,
          page: appPage,
          setPage: setAppPage,
          columns: appColumns,
          loading: appLoading,
        };
      case "credit":
        return {
          data: creditData,
          total: creditTotal,
          page: creditPage,
          setPage: setCreditPage,
          columns: creditColumns,
          loading: creditLoading,
        };
      default:
        return { data: [], total: 0, page: 0, setPage: () => {}, columns: [], loading: false };
    }
  };

  const { data, total, page, setPage, columns, loading } = getTableProps();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      // Reset all pages to 0 whenever search query changes
      setB2bPage(0);
      setPosPage(0);
      setAppPage(0);
      setCreditPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchBarChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <Navbar pageTitle={"Customers"}>
      <main className="customer-table-v2___main-container">
        {/* ── Header section: title left, actions right ── */}
        <section className="customer-table-v2___header-section">
          <div className="customer-table-v2___header-inner">
            {/* Left: Page title */}
            <div className="customer-table-v2___header-title-wrap">
              <h1 className="customer-table-v2___header-title">Customers</h1>
            </div>

            {/* Right: Search + Loyalty Points */}
            <div className="customer-table-v2___header-actions">
              {/* Search input */}
              <div className="customer-table-v2___search-wrap">
                <span className="customer-table-v2___search-icon">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  placeholder="Search by name or phone number"
                  className="customer-table-v2___search-input"
                  data-testid="input-customers-search"
                  onChange={(e) => handleSearchBarChange(e)}
                />
              </div>
              <button
                type="button"
                className="customer-table-v2___filter-btn"
                data-testid="button-customers-filter"
                onClick={() => showSnackbar("Feature Comming Soon", "info")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />
                </svg>
                Filter
                <span className="customer-table-v2___chevron">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </span>
              </button>
              <button
                type="button"
                className="customer-table-v2___loyalty-btn"
                data-testid="button-loyalty-points"
                onClick={() => {
                  // navigate("/products/points")
                  showSnackbar("Feature Comming Soon", "info");
                }}
              >
                <div className="btn-contents">
                  <PlusIcon width={15} height={15} />
                  <>New</>
                </div>
              </button>
            </div>
          </div>
        </section>

        <section className="customer-table-grid___section-card">
          <div className="customer-table-v2___tabs-row">
            <CustomerCustomTab tabs={customerTabs} defaultTab="b2b" onChange={setActiveTab} />
          </div>

          <CustomerDataTable
            activeTab={activeTab}
            columns={columns}
            data={data}
            total={total}
            page={page}
            pageSize={10}
            onPageChange={setPage}
            loading={loading}
          />
        </section>
      </main>
    </Navbar>
  );
};

export default CustomerTableV2;
